import { useState } from 'react'
import DialogContent from '@mui/material/DialogContent'
import Typography from '@mui/material/Typography'
import IconNode from './IconNode'
import TextFieldForm from './TextFieldForm'
import CreateRefusal from './CreateRefusal'
import useOrganizations from '../models/useOrganizations'
import Modal from './Modal'
import { useModalClose } from '../hooks/useModalClose'
import { limitRefusal, type LimitRefusal } from '../utilities/limitRefusal'

type Props = {
  inline?: boolean
  onClose?: () => void
}

export default function ModalOrg({ inline, onClose }: Readonly<Props>) {
  const [name, setName] = useState('')
  const [error, setError] = useState<string>()
  const [refusal, setRefusal] = useState<Pick<LimitRefusal, 'message' | 'upgradeUrl'> | null>(null)
  const { open, handleClose } = useModalClose('/', inline)
  const { create } = useOrganizations()

  const close = async () => {
    onClose?.()
    await handleClose()
  }

  const handleSubmit = async (value: string) => {
    const trimmed = value.trim()
    if (!trimmed) return
    setError(undefined)
    setRefusal(null)
    try {
      await create(trimmed)
      await close()
    } catch (e) {
      // ⚠⚠ `org_create` REACHES THE SAME GATE AS `network_create`, with the `organizations` limit
      // type (app#296). The refusal is a whole paragraph — "you already own 3 free organizations
      // … upgrade one to a paid plan, or have someone else own the next one" — and it was being
      // squeezed into the field's `helperText`, in red, with no way to act on it. A limit refusal
      // now gets the Alert and its Upgrade action; a field-level failure (a name too long, an
      // empty name) keeps the helper text, which is where a field complaint belongs.
      console.error('org_create failed', e)
      const limit = limitRefusal(e)
      if (limit) setRefusal(limit)
      else setError(e instanceof Error ? e.message : 'Failed to create organization')
    }
  }

  return (
    <Modal
      open={open}
      inline={inline}
      aria-label="New Organization"
      maxWidth="sm"
      onClose={close}
      color="primary.main"
      icon={<IconNode type="network" width={54} height={54} outline plus />}
    >
      <DialogContent>
        <Typography
          variant="subtitle1"
          color="primary"
          sx={{
            flexGrow: 1,
            mb: 3
          }}>
          New Organization
        </Typography>
        <CreateRefusal message={refusal?.message} upgradeUrl={refusal?.upgradeUrl} />
        <TextFieldForm
          autoFocus
          label="Organization Name"
          placeholder="Acme Inc"
          value={name}
          onChange={setName}
          onSubmit={handleSubmit}
          error={!!error}
          helperText={error}
        />
      </DialogContent>
    </Modal>
  )
}
